import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ManageOrderData, CustomerOrder, TaxBreakdownEntry } from "../../../common/jsonRpcTypes";
import AlertBanner from "../shared/AlertBanner";
import PrimaryButton from "../buttons/primaryButton";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import { isGroupMemberPaymentMenu } from "../../utils/preorderSettingsUtils";
import { useVenueCurrency } from "../hooks/useVenueCurrency";

interface OrderSummaryProps {
  manageOrderData: ManageOrderData;
  onCompletePreorders: () => void;
  canComplete: boolean;
  submitting: boolean;
  minGroupMemberOrdersNotMet?: boolean;
}

interface ServiceChargeGroup {
  percentage: number;
  amount: number;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

function calculateOrderTotals(
  pendingOrders: CustomerOrder[],
  fallbackServiceChargePercentage?: number,
  fallbackServiceChargeAmount?: number,
) {
  let subtotal = 0;
  let totalServiceCharge = 0;
  const serviceChargesByPercentage: Map<number, number> = new Map();
  let hasPerOrderServiceCharges = false;

  for (const order of pendingOrders) {
    subtotal += order.total_cost ?? 0;

    const percentage = order.service_charge_percentage ?? 0;
    const amount = order.service_charge_amount ?? 0;

    if (percentage > 0 && amount > 0) {
      hasPerOrderServiceCharges = true;
      totalServiceCharge += amount;
      const existing = serviceChargesByPercentage.get(percentage) ?? 0;
      serviceChargesByPercentage.set(percentage, existing + amount);
    }
  }

  // Fallback to API-level service charge if no per-order service charges found
  if (!hasPerOrderServiceCharges && fallbackServiceChargeAmount && fallbackServiceChargeAmount > 0) {
    totalServiceCharge = fallbackServiceChargeAmount;
    const percentage = fallbackServiceChargePercentage ?? 0;
    if (percentage > 0) {
      serviceChargesByPercentage.set(percentage, fallbackServiceChargeAmount);
    }
  }

  const serviceChargeGroups: ServiceChargeGroup[] = [];
  for (const [percentage, amount] of serviceChargesByPercentage.entries()) {
    serviceChargeGroups.push({
      percentage,
      amount: roundCurrency(amount),
    });
  }

  serviceChargeGroups.sort((a, b) => a.percentage - b.percentage);

  return {
    subtotal: roundCurrency(subtotal),
    totalServiceCharge: roundCurrency(totalServiceCharge),
    grandTotal: roundCurrency(subtotal + totalServiceCharge),
    serviceChargeGroups,
  };
}

interface TaxAggregation {
  totalTax: number;
  breakdown: TaxBreakdownEntry[];
}

/**
 * Aggregates tax from the per-order `tax_breakdown` fields, scoped to the same
 * orders used for the Subtotal/Net and Service Charge rows. Returns `null` when
 * none of the given orders carry per-order tax data, so the caller can fall back
 * to the API-provided aggregate. Computing tax from the same scope as the rest of
 * the summary avoids mixing a locally-scoped net with an API aggregate that may
 * cover a different set of orders.
 */
export function aggregatePendingTax(orders: CustomerOrder[]): TaxAggregation | null {
  const amountByLabel = new Map<string, number>();
  let totalTax = 0;
  let hasPerOrderTax = false;

  for (const order of orders) {
    if (!Array.isArray(order.tax_breakdown) || order.tax_breakdown.length === 0) continue;
    hasPerOrderTax = true;
    for (const entry of order.tax_breakdown) {
      amountByLabel.set(entry.label, (amountByLabel.get(entry.label) ?? 0) + entry.amount);
      totalTax += entry.amount;
    }
  }

  if (!hasPerOrderTax) return null;

  const breakdown: TaxBreakdownEntry[] = Array.from(amountByLabel.entries()).map(([label, amount]) => ({
    label,
    amount: roundCurrency(amount),
  }));

  return { totalTax: roundCurrency(totalTax), breakdown };
}

export function getPaymentAlertKey(
  menus: { menu_id: string; who_pays?: string; payment_required: string }[],
  pendingMenuIds: Set<string>,
): string | null {
  const menusWithItems = menus.filter((m) => pendingMenuIds.has(m.menu_id));
  if (menusWithItems.length === 0) return null;

  const hasNonePayment = menusWithItems.some((m) => !m.payment_required);
  const hasLeadBooker = menusWithItems.some((m) => !!m.payment_required && m.who_pays === "lead_booker");
  // Group-member payment includes menus that require payment but have no explicit
  // who_pays value — these default to group member (the lead booker does not pay).
  const hasGroupMember = menusWithItems.some((m) => isGroupMemberPaymentMenu(m));

  if (hasGroupMember && hasLeadBooker) return "group_and_lead_pay_alert_message";
  if (hasLeadBooker && !hasNonePayment && !hasGroupMember) return null;
  if (hasGroupMember && !hasNonePayment && !hasLeadBooker) return "payment_group_members_paid";
  if (hasNonePayment && !hasLeadBooker && !hasGroupMember) return "payment_none_alert_message";
  if (hasNonePayment && hasLeadBooker) return "payment_required_alert_message";
  if (hasNonePayment && hasGroupMember) return "none_and_group_members_pay_alert";

  return null;
}

const OrderSummary: React.FC<OrderSummaryProps> = ({
  manageOrderData,
  onCompletePreorders,
  canComplete,
  submitting,
  minGroupMemberOrdersNotMet = false,
}) => {
  const { t } = useTranslation();
  const { currency, countryCode } = useVenueCurrency(manageOrderData.booking.venue);

  const pendingOrders = manageOrderData.orders.filter((order) => order.status === "pending");

  const alreadyPaidAmount = manageOrderData.orders.reduce((sum, order) => {
    if (order.payment_ref) {
      return sum + (order.amount_paid ?? 0);
    }
    return sum;
  }, 0);

  const { subtotal, grandTotal, serviceChargeGroups } = useMemo(
    () =>
      calculateOrderTotals(pendingOrders, manageOrderData.serviceChargePercentage, manageOrderData.serviceChargeAmount),
    [pendingOrders, manageOrderData.serviceChargePercentage, manageOrderData.serviceChargeAmount],
  );

  const isTaxExclusive =
    manageOrderData.tax_inclusive_orders === false &&
    Array.isArray(manageOrderData.aggregated_tax_breakdown) &&
    manageOrderData.aggregated_tax_breakdown.length > 0;

  // Prefer tax aggregated from the per-order breakdowns of the pending orders so
  // it shares the same scope as the Net/Service Charge rows. Fall back to the
  // API-provided aggregate when per-order tax data isn't available.
  const pendingTax = useMemo(() => aggregatePendingTax(pendingOrders), [pendingOrders]);
  const taxBreakdown = pendingTax?.breakdown ?? manageOrderData.aggregated_tax_breakdown ?? [];
  const totalTax = pendingTax?.totalTax ?? manageOrderData.aggregated_total_tax ?? 0;

  // Use the locally-computed grand total (scoped to pending orders, matching the
  // displayed Subtotal/Service Charge rows) as the base. The API doesn't provide
  // an explicit gross total, so tax addition is still needed here for tax-exclusive venues.
  const grossTotal = isTaxExclusive ? roundCurrency(grandTotal + totalTax) : grandTotal;
  const taxLabel = manageOrderData.tax_config?.tax_label ?? "VAT";

  const pendingMenuIds = useMemo(() => {
    const ids = new Set<string>();
    for (const order of pendingOrders) {
      for (const item of order.items) {
        ids.add(item.menu_id);
      }
    }
    return ids;
  }, [pendingOrders]);

  const paymentAlertKey = getPaymentAlertKey(manageOrderData.preorderSettings.menus, pendingMenuIds);

  let paymentAlertAmount: string | undefined;
  if (paymentAlertKey === "payment_required_alert_message" || paymentAlertKey === "group_and_lead_pay_alert_message") {
    paymentAlertAmount = formatCurrency(manageOrderData.requiredPaymentAmount, currency, countryCode);
  }

  if (pendingOrders.length === 0) {
    return null;
  }

  return (
    <div id="gc-myo-order-summary" className="order-summary">
      <div className="gcw:p-bs-3">
        <div className="summary-details">
          {paymentAlertKey && (
            <AlertBanner
              className="payment-info-alert gcw:mb-2"
              translatedKey={paymentAlertKey}
              translationParams={{ amount: paymentAlertAmount }}
              textClassName="alert-text"
            />
          )}

          <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2">
            <div className="gcw:text-order-details">
              {isTaxExclusive ? t("net_label", "Net") : t("subtotal", "Subtotal")}:
            </div>
            <div className="gcw:text-order-details gcw:text-right gcw:text-text-primary">
              {formatCurrency(subtotal, currency, countryCode)}
            </div>
          </div>

          {manageOrderData.totalDiscount > 0 && (
            <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2 gcw:text-success">
              <div className="gcw:text-order-details">{t("discount", "Discount")}:</div>
              <div className="gcw:text-order-details gcw:text-right gcw:text-text-primary">
                -{formatCurrency(manageOrderData.totalDiscount, currency, countryCode)}
              </div>
            </div>
          )}

          {serviceChargeGroups.map((group) => (
            <div
              key={`service-charge-${group.percentage}`}
              className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2"
            >
              <div className="gcw:text-order-details gcw:text-text-primary">
                {t("service_charge", "Service Charge")} ({group.percentage}%):
              </div>
              <div className="gcw:text-order-details gcw:text-right gcw:text-text-primary">
                {formatCurrency(group.amount, currency, countryCode)}
              </div>
            </div>
          ))}

          {isTaxExclusive &&
            taxBreakdown.map((entry) => (
              <div key={entry.label} className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2">
                <div className="gcw:text-order-details gcw:text-text-primary">{entry.label}:</div>
                <div className="gcw:text-order-details gcw:text-right gcw:text-text-primary">
                  {formatCurrency(entry.amount, currency, countryCode)}
                </div>
              </div>
            ))}

          {(paymentAlertKey === "group_and_lead_pay_alert_message" ||
            paymentAlertKey === "none_and_group_members_pay_alert") && (
            <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2">
              <div className="gcw:text-order-details gcw:text-text-primary">{t("paid_amount", "Already paid")}:</div>
              <div className="gcw:text-order-details gcw:text-right gcw:text-text-primary">
                - {formatCurrency(alreadyPaidAmount, currency, countryCode)}
              </div>
            </div>
          )}

          {paymentAlertKey === "group_and_lead_pay_alert_message" ||
          paymentAlertKey === "none_and_group_members_pay_alert" ? (
            <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-2">
              <div className="gcw:text-subtitle gcw:font-bold">{t("total_due", "Total Due")}:</div>
              <div className="gcw:text-text-primary gcw:font-bold">
                {formatCurrency(manageOrderData.requiredPaymentAmount, currency, countryCode)}
              </div>
            </div>
          ) : (
            <>
              {manageOrderData.requiredPaymentAmount !== grossTotal && manageOrderData.requiredPaymentAmount > 0 && (
                <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-1">
                  <div className="gcw:text-subtitle gcw:font-bold">{t("total_due", "Total Due")}:</div>
                  <div className="gcw:text-text-primary gcw:font-bold">
                    {formatCurrency(manageOrderData.requiredPaymentAmount, currency, countryCode)}
                  </div>
                </div>
              )}
              <div className="gcw:flex gcw:items-center gcw:justify-between gcw:mb-1">
                <p className="gcw:text-subtitle gcw:font-bold">
                  {t("total", "Total")}
                  {!isTaxExclusive && ` (${t("inc_vat", "inc {{taxLabel}}", { taxLabel })})`}:
                </p>
                <p className="gcw:text-text-primary gcw:font-bold">
                  {formatCurrency(grossTotal, currency, countryCode)}
                </p>
              </div>
            </>
          )}
        </div>

        <PrimaryButton
          text={
            manageOrderData.requiredPaymentAmount > 0
              ? t("pay_now", "Pay now")
              : t("place_pre_order", "Place Pre-Order")
          }
          onClick={onCompletePreorders}
          className="gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-white gcw:border gcw:border-primary gcw:px-bs-3 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 pay-button gcw:w-full gcw:my-2"
          disabled={!canComplete}
        />

        {!canComplete && !submitting && minGroupMemberOrdersNotMet && (
          <small className="gcw:text-error gcw:mt-2 gcw:block">
            {t("min_group_member_orders_not_met", "The minimum required number of group orders has not been reached.")}
          </small>
        )}

        {!canComplete && !submitting && !minGroupMemberOrdersNotMet && (
          <small className="gcw:text-text-muted gcw:mt-2 gcw:block">
            {t("complete_preorder_disabled_help", "Please ensure you have pending orders to complete.")}
          </small>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
